import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { TPriceColumn } from "libraries/component/booking-information/components/booking-price-card/ITablePrice";
import { IPricesTable, PricesTableService } from "libraries/component/booking-information/components/booking-price-card/services/prices-table.service";
import { Yii } from "libraries/interfaces/Yii";
import { PriceInBookingType } from "@afidium/ts-facade";
import { EColumnType } from "libraries/component/booking-information/components/booking-price-card/booking-price-card.enum";
import { BookingType } from "@afidium/ts-facade/model/bookingType";
import { SectorInBookingType } from "@afidium/ts-facade/model/sectorInBookingType";
import { TravellerType } from "@afidium/ts-facade/model/travellerType";

export class Wording {
    public noPrice: string = Yii.t('wui-shared-js', 'Aucune information de tarification à afficher');
    public totalPrice: string = Yii.t('wui-shared-js', 'Total');
}

export enum ELineType {
    rate = "Rate",
    total = "Total"
}


@Component({
    selector: 'prices-table',
    template: `
        <table mat-table
               *ngIf="dataSource"
               [dataSource]="dataSource"
               [hidden]="selectedPrice">
            <ng-container *ngFor="let column of displayedColumns; let first = first; let index = index;">
                <div [matColumnDef]="column" [sticky]="first">
                    <th mat-header-cell *matHeaderCellDef
                        [class.first-column]="first"
                        [class.other-column-width]="!first">
                        <ng-container [ngSwitch]="column">
                            <ng-container *ngSwitchCase="eColumnType.rateTitle"></ng-container>
                            <ng-container *ngSwitchCase="eColumnType.totalValue"><span
                                    class="bold">{{ wording.totalPrice }}</span>
                            </ng-container>
                            <ng-container *ngSwitchDefault>
                                <span class="bold"
                                      (mouseenter)="onMouseEnter(column)"
                                      (mousemove)="onMouseMove($event)"
                                      (mouseleave)="onMouseLeave()">
                                    {{ column | computeTravellerTitle : booking : booking.travellerArray }}
                                </span>
                                <mat-card *ngIf="hoveredTravellerSignature === column" class="hovered-card">
                                    <span class="bold">{{ showHoveredTravellerName(column) }}</span>
                                </mat-card>
                            </ng-container>
                        </ng-container>
                    </th>

                    <td mat-cell
                        *matCellDef="let element; let i = index;"
                        [ngClass]="{'align-price-right' : !first}">
                        <ng-container *ngIf="first; else notFirst">
                            <rate-sectors *ngIf="element[column] !== lineType.total; else total"
                                          [sectors]="sectorsByRateId[i]"
                                          [rateType]="element['rateXRole']"
                                          [rateTitle]="element[column]"
                                          [rateId]="element['rateId']"
                                          (onRateSelect)="changeRateSelection($event)"></rate-sectors>
                            <ng-template #total>
                                <div class="bold">{{ element[column] }}</div>
                            </ng-template>
                        </ng-container>
                        <ng-template #notFirst>
                            <ng-container *ngIf="column === eColumnType.totalValue; else notTotal">
            <span class="bold"
                  [innerHTML]="element[column]?.amount | priceRealAmount : element[column]?.currencyCode">  </span>
                            </ng-container>
                            <ng-template #notTotal>
                                <span [innerHTML]="element[column]?.unitAmount | priceRealAmount : element[column]?.currencyCode">  </span>
                            </ng-template>
                        </ng-template>
                    </td>

                </div>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
        </table>

        <div *ngIf="!booking" class="noInformation">{{ wording.noPrice }}</div>
    `,
    styles: [`
        .bold {
            margin: 0;
            font-size: 1rem;
            font-weight: bold;
        }

        table tr.mat-header-row th.mat-header-cell,
        table .mat-row .mat-cell {
            padding: .75rem;
            border-color: rgba(189, 195, 199, 0.2);
            border-style: solid;
            border-width: 1px 1px 1px 1px;
            font-size: 1rem;
        }

        .first-column {
            background-color: white !important;
        }

        .hovered-card {
            position: absolute;
            width: auto;
            z-index: 1000;
        }

        .align-price-right {
            text-align: right;
        }

    `]
})

export class PricesTableComponent implements OnChanges {

    @Input()
    public booking: BookingType;

    @Input()
    public costOrRetail: string;

    public selectedPrice: PriceInBookingType;
    public sectors: SectorInBookingType[];
    public wording: Wording = new Wording();
    public dataSource: IPricesTable[];
    public displayedColumns: string[] = [];
    public columns: TPriceColumn[];
    public sectorsByRateId: SectorInBookingType[][] = [];
    public eColumnType: typeof EColumnType = EColumnType;
    public lineType: typeof ELineType = ELineType;
    public mouseX: number;
    public mouseY: number;
    public hoveredTravellerSignature: string | null = null;


    public constructor(
        private readonly pricesTablesService: PricesTableService,
    ) {
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes?.booking?.currentValue) {
            this.displayedColumns = [];
            this.dataSource = this.pricesTablesService
                .constructData(this.booking?.priceArray, this.booking?.travellerArray, this.costOrRetail, this.booking?.total, this.booking?.serviceArray);
            if (this.dataSource?.length) {
                Object.keys(this.dataSource[0]).forEach((key: string): void => {
                    if (key !== EColumnType.rateId && key !== EColumnType.rateXRole) {
                        this.displayedColumns.push(key);
                    }
                });
                this.dataSource.filter((item: IPricesTable): string => item[EColumnType.rateId]).forEach((item: IPricesTable, index: number): void => {
                    this.sectorsByRateId[index] = this.pricesTablesService
                        .getSectorsBySignature(
                            this.pricesTablesService.getRateByXRole(item[EColumnType.rateId], this.booking, this.costOrRetail),
                            this.booking,
                            this.costOrRetail
                        );
                });
            }
        }
    }

    public changeRateSelection(selectedRateId: string): void {
        this.pricesTablesService.setRateSelected(selectedRateId);
    }

    public showHoveredTravellerName(signature: string): string | null {
        const travellersToFormat: TravellerType[] = this.booking ? this.booking?.travellerArray : null;
        const traveller: TravellerType = travellersToFormat?.find((travellerItem: TravellerType): boolean => {
            return travellerItem?.signature === signature;
        });
        if (traveller) {
            return `${traveller.firstName ?? ''} ${traveller.lastName ?? ''}`;
        }
        return null;
    }

    public onMouseEnter(column: string): void {
        this.hoveredTravellerSignature = column;
    }


    public onMouseMove(event: MouseEvent): void {
        this.mouseX = event.clientX;
        this.mouseY = event.clientY;
    }

    public onMouseLeave(): void {
        this.hoveredTravellerSignature = null;
    }
}
